<template>
    <div>
        <auth-container :page="page">
            <h2 class="mb-2">Funds Spent</h2>

            <p>
                Total invoiced = sum of all labor, non-labor, and subconsultant amounts for invoiced in the selected date range.<br>
                Total reimbursements = sum of all state, federal, and sponsor amounts for agreement reimbursements in the selected date range.
            </p>

            <b-card bg-variant="light" class="mb-4">
                <h5>Customize Date Range</h5>
                <div class="d-flex align-items-center justify-content-start">
                    <div class="mr-2">
                        <b-form-select v-model="advancedFilter.date.type" :options="dateTypeOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'FY' || advancedFilter.date.type === 'CY'">
                        <b-form-input type="number" step="any" v-model="advancedFilter.date.year" @input="changeDateFilterType" style="max-width:200px"></b-form-input>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'FY'">
                        <b-form-select v-model="advancedFilter.date.quarter" :options="fiscalQuarterOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'CY'">
                        <b-form-select v-model="advancedFilter.date.quarter" :options="calendarQuarterOptions" @change="changeDateFilterType" style="max-width:300px"></b-form-select>
                    </div>

                    <div class="mr-2" v-if="advancedFilter.date.type === 'Custom'">
                        <b-input-group>
                            <b-form-input type="date" v-model="advancedFilter.date.start" @input="checkDateAndGet"></b-form-input>

                            <b-input-group-append class="pointer">
                                <b-input-group-text @click="advancedFilter.date.start = null; get()">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div v-if="advancedFilter.date.type === 'Custom'">
                        <b-input-group>
                            <b-form-input type="date" v-model="advancedFilter.date.end" @input="checkDateAndGet"></b-form-input>

                            <b-input-group-append class="pointer">
                                <b-input-group-text @click="advancedFilter.date.end = null; get()">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </b-input-group-text>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                </div>

                <h5 class="mt-4">
                    Showing: 
                    <span v-if="submittedDate.start === null && submittedDate.end === null">All Time</span>
                    <span v-else>{{submittedDate.start | date('M/d/YYYY', 'Beginning')}} - {{submittedDate.end | date('M/d/YYYY', 'Present')}}</span>
                </h5>
                <b-row class="mt-2">
                    <b-col md="2">Total Invoiced:</b-col>
                    <b-col md>${{totalInvoiced | number(2)}}</b-col>
                </b-row>
                <b-row class="mt-1">
                    <b-col md="2">Total Reimbursements:</b-col>
                    <b-col md>${{totalReimbursements | number(2)}}</b-col>
                </b-row>
            </b-card>

            <h3>Funds Spent by Project</h3>
            <div class="mx-auto">
                <b-row class="mb-3" align-v="end">
                    <b-col lg="6">
                        <b-form inline>
                            <b-input-group>
                                <b-form-input v-model="table.filter" placeholder="Search..." @input="filterChg"></b-form-input>

                                <b-input-group-append class="pointer mr-2">
                                    <b-input-group-text @click="clearFilter">
                                        <font-awesome-icon :icon="['fas', 'times']" />
                                    </b-input-group-text>
                                </b-input-group-append>
                            </b-input-group>
                        </b-form>
                    </b-col>
                    <b-col lg="6" class="text-right">
                        <div class="ml-auto">
                            Showing {{showingFirst}} - {{showingLast}}
                            of {{table.total}}
                        </div>
                    </b-col>
                </b-row>

                <b-table striped responsive table-class="table-fixed-height border-bottom table-grid" sticky-header="60vh"
                         small show-empty
                         :items="items" :busy="table.loading"
                         :fields="table.fields"
                         :no-local-sorting="true"
                         :sort-by.sync="table.sort"
                         :sort-desc.sync="table.reverse"
                         @sort-changed="sortChg">
                    <template #table-busy>
                        <div class="text-center my-4">
                            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
                        </div>
                    </template>
                    <template #cell(damId)="data">
                        <router-link :to="`/dams/${data.item.damId}`">{{data.item.nid_id}}</router-link>
                    </template>
                    <template #cell(projectNumber)="data">
                        <router-link :to="`/sr/projects/${data.item.id}`">{{data.item.projectNumber}}</router-link>
                    </template>
                    <template #empty>
                        <h4>No records found.</h4>
                    </template>
                    <template #emptyfiltered>
                        <h4>No records found matching "{{table.filter}}".</h4>
                    </template>
                </b-table>
            </div>

            <fixed-action-bar>
                <json-to-csv :api-url="exportUrl" :filename="apiUrl" class="mr-2" />
                <b-pagination class="ml-auto mb-0" v-if="table.total > table.itemsPerPage" size="md" :total-rows="table.total" v-model="table.currentPage" :per-page="table.itemsPerPage" @change="pageChg" />
            </fixed-action-bar>
        </auth-container>
        <router-view></router-view>
    </div>
</template>

<script>
    import _ from 'underscore';

    export default {
        name: 'SrReportFundsSpent',
        data() {
            return {
                apiUrl: 'srreports/fundsspent',
                page: {
                    errors: [],
                    loading: true,
                    showLogin: false,
                    saveErrors: [],
                    showModal: false,
                    showModal1: false,
                    showModal2: false
                },
                table: {
                    fields: [
                        { key: 'projectNumber', label: 'TPN', sortable: true },
                        { key: 'damId', label: 'NID_ID', sortable: true },
                        { key: 'status', sortable: true },
                        { key: 'category', sortable: true },
                        { key: 'currentPhase', sortable: true },
                        { key: 'name', sortable: true },
                        { key: 'totalInvoiced', label: 'Total Invoiced', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
                        { key: 'totalReimbursements', label: 'Total Reimbursed', sortable: true, class: 'text-right', formatter: (value) => { return this.money(value); } },
                    ],
                    sort: 'projectNumber',
                    reverse: false,
                    itemsPerPage: 100,
                    filter: null,
                    currentPage: 1,
                    total: 0,
                    loading: false,
                    showAdvancedFilter: false
                },
                items: [],
                advancedFilter: {
                    date: {
                        start: null,
                        end: null,
                        type: 'All',
                        year: 0,
                        quarter: 'All'
                    },
                },
                submittedDate: {
                    start: null,
                    end: null
                },
                totalInvoiced: 0,
                totalReimbursements: 0
            }
        },
        computed: {
            showingFirst() {
                return this.table.total === 0 ? 0 : (this.table.currentPage - 1) * this.table.itemsPerPage + 1
            },
            showingLast() {
                var max = (this.table.currentPage - 1) * this.table.itemsPerPage + this.table.itemsPerPage
                return max > this.table.total ? this.table.total : max;
            },
            exportUrl() {
                let filterComplex = this.getFilterComplex('?');
                return `${this.apiUrl}export${filterComplex}`
            }
        },
        async created() {
            this.advancedFilter.date.year = this.currentFiscalYear();
            await this.get();
        },
        watch: {
            '$route': 'get',
        },
        methods: {
            getDb() {
                let sort = this.table.reverse ? this.table.sort + '_desc' : this.table.sort;
                let filter = this.table.filter !== null ? `&filter=${encodeURIComponent(this.table.filter)}` : '';

                let filterComplex = this.getFilterComplex();

                return this.$http.get(`${this.apiUrl}?sort=${sort}&page=${this.table.currentPage}&perPage=${this.table.itemsPerPage}${filter}${filterComplex}`, this.getTokenHeader());
            },
            getFilterComplex(startChar = '&') {
                let filterComplex = '';
                let filterValues = [];
                let stringFields = [];
                let twoPartFields = ['date'];

                for (let f of stringFields) {
                    if (!this.isNullOrEmpty(this.advancedFilter[f])) {
                        filterValues.push(`${f}:${this.advancedFilter[f]}`);
                    }
                }

                for (let f of twoPartFields) {
                    if (!this.isNullOrEmpty(this.advancedFilter[f].start) || !this.isNullOrEmpty(this.advancedFilter[f].end)) {
                        let s = this.advancedFilter[f].start;
                        let e = this.advancedFilter[f].end;
                        s = s === null ? 'any' : s;
                        e = e === null ? 'any' : e;
                        filterValues.push(`${f}:${s},${e}`);
                    }
                }

                if (filterValues.length > 0) filterComplex = startChar + 'filterComplex=' + filterValues.join('|');
                return filterComplex;
            },
            async filterChg() {
                this.table.currentPage = 1;
                _.debounce(await this.get(), 500);
            },
            async pageChg(page) {
                this.table.currentPage = page;
                await this.get();
            },
            async sortChg(ctx) {
                this.table.sort = ctx.sortBy;
                this.table.reverse = ctx.sortDesc;
                this.table.specialSort = undefined;

                await this.get();
            },
            async get() {
                this.page.errors = [];
                this.table.loading = true;

                try {
                    const response = await this.getDb();
                    this.log(response.data);

                    if (response.data.items.length == 0 && this.table.currentPage > 1) {
                        this.table.currentPage--;
                        await this.get();
                    }

                    this.items = response.data.items;
                    this.table.total = response.data.total;
                    this.totalInvoiced = response.data.totalInvoiced;
                    this.totalReimbursements = response.data.totalReimbursements;
                    this.submittedDate.start = this.advancedFilter.date.start;
                    this.submittedDate.end = this.advancedFilter.date.end;
                } catch (error) {
                    if (this.isApiUnauthorized(error)) this.page.showLogin = true;
                    else this.page.errors = this.logError(error);
                }

                this.table.loading = false;
                this.page.loading = false;
            },
            async clearFilter() {
                this.table.filter = null;
                await this.get();
            },
            async clearAdvancedFilter() {
                this.advancedFilter = {
                    date: {
                        start: null,
                        end: null,
                        type: 'All',
                        year: 0,
                        quarter: 'All'
                    },
                };

                await this.get();
            },
            async changeDateFilterType() {
                if (this.advancedFilter.date.year !== 0 && this.advancedFilter.date.year < 1000) return;

                switch (this.advancedFilter.date.type) {
                    case 'Custom':
                        return;
                    case 'FY':
                        if (this.advancedFilter.date.year === 0) this.advancedFilter.date.year = this.currentFiscalYear();
                        break;
                    case 'CY':
                        if (this.advancedFilter.date.year === 0) this.advancedFilter.date.year = this.currentFiscalYear();
                        break;
                    default:
                        this.advancedFilter.date.start = null;
                        this.advancedFilter.date.end = null;
                        await this.get();
                        return;
                }

                let d = this.getDatesFromQuarter(this.advancedFilter.date.year, this.advancedFilter.date.quarter, this.advancedFilter.date.type)
                this.advancedFilter.date.start = d[0];
                this.advancedFilter.date.end = d[1];
                await this.get();
            },
            async checkDateAndGet() {
                if (this.advancedFilter.date.start !== null && !this.isValidDate(this.advancedFilter.date.start)) return;
                if (this.advancedFilter.date.end !== null && !this.isValidDate(this.advancedFilter.date.end)) return;
                await this.get();
            }
        }
    }
</script>